import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../utils/apiConfig';
import { auth, db } from '../services/firebase';
import { collection, onSnapshot, query, orderBy, where, getDocs, doc, getDoc } from 'firebase/firestore';
import {
    Box, Typography, Card, CardContent, Button, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, Chip, IconButton, Dialog,
    DialogTitle, DialogContent, DialogActions, TextField, Grid, FormControl,
    InputLabel, Select, MenuItem, InputAdornment
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import styles from '../styles/Dashboard.module.css';

const GerenciarCondominios = () => {
    const [condominios, setCondominios] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedCondo, setSelectedCondo] = useState(null);

    // Form State
    const [formData, setFormData] = useState({
        nome: '', endereco: '', cnpj: '',
        inicio_contrato: '', fim_contrato: '',
        mensagem_suspensao: '',
        administradora_email: '', // New Field
        sindico_nome: '', sindico_email: '' // Only for creation
    });
    const [newSindicoData, setNewSindicoData] = useState({ nome: '', email: '' });
    const [currentSindico, setCurrentSindico] = useState(null); // To assist display

    useEffect(() => {
        // Listen to condominios collection
        const q = query(collection(db, 'tb_condominios'), orderBy('nome'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setCondominios(list);
        });
        return () => unsubscribe();
    }, []);

    const handleOpenCreate = () => {
        setIsEdit(false);
        setSelectedCondo(null);
        setFormData({ nome: '', endereco: '', cnpj: '', inicio_contrato: '', fim_contrato: '', mensagem_suspensao: '', administradora_email: '', sindico_nome: '', sindico_email: '' });
        setOpenDialog(true);
    };

    const fetchCurrentSindico = async (condoId) => {
        try {
            const q = query(collection(db, 'tb_usuarios'), where('condominio_id', '==', condoId), where('papel', '==', 'sindico'));
            const querySnapshot = await getDocs(q);
            if (!querySnapshot.empty) {
                const data = querySnapshot.docs[0].data();
                setCurrentSindico({ nome: data.nome, email: data.email });
            } else {
                setCurrentSindico(null);
            }
        } catch (err) {
            console.error("Erro ao buscar sindico", err);
            setCurrentSindico(null);
        }
    };

    const fetchAdministradoraEmail = async (adminId) => {
        if (!adminId) return '';
        try {
            const docRef = await getDocs(query(collection(db, 'tb_usuarios'), where('__name__', '==', adminId)));
            // Or getDoc but I need to import getDoc with db.doc... 
            // Actually, I can use a query or if I could import doc/getDoc.
            // But simpler:
            // Wait, I can't read `tb_usuarios` freely unless I am authenticated. I am admin_sistema.
            // rules allow read if request.auth != null.
            // But getting by ID is better.
            // Let's use getDocs with query by ID just to be safe with existing imports.
            // Actually `doc(db, 'tb_usuarios', adminId)` is better but I need to ensure `doc` and `getDoc` are imported.
            // They are NOT imported in the original file I see above?
            // Ah, `import { collection, onSnapshot, query, orderBy, where, getDocs }`
            // I missed `doc, getDoc`.
            // I will assume I need to fetch it via query for now or add the import.
            // Let's use query.
            // `where(documentId(), '==', adminId)` requires `documentId` import.
            // I'll just use a loop on clientside? No.
            // I will Update imports first? No, I'll use `onSnapshot` inside `useEffect` logic?
            // Let's just rely on `getDocs`.
            // Actually I'll use `getDocs(query(collection(db, 'tb_usuarios')))` and filter? No.
            // I will add `doc` and `getDoc` to imports in the SAME chunk if possible? No, different lines.
            // I will skip fetching for now and just set empty, and if the user wants to update, they type it.
            // Better: I'll use the replace to ADD the imports.
            return '';
        } catch (e) { return ''; }
    };

    const handleOpenEdit = async (condo) => {
        setIsEdit(true);
        setSelectedCondo(condo);

        let adminEmail = '';
        if (condo.administradora_id) {
            try {
                const userSnap = await getDoc(doc(db, 'tb_usuarios', condo.administradora_id));
                if (userSnap.exists()) {
                    adminEmail = userSnap.data().email;
                }
            } catch (e) {
                console.error('Error fetching admin email', e);
            }
        }

        setFormData({
            nome: condo.nome,
            endereco: condo.endereco,
            cnpj: condo.cnpj,
            inicio_contrato: condo.inicio_contrato || '',
            fim_contrato: condo.fim_contrato || '',
            mensagem_suspensao: condo.mensagem_suspensao || '',
            administradora_email: adminEmail,
            sindico_nome: '', sindico_email: ''
        });
        setNewSindicoData({ nome: '', email: '' });
        fetchCurrentSindico(condo.id);
        setOpenDialog(true);
    };

    const handleChangeSindico = async () => {
        if (!newSindicoData.nome || !newSindicoData.email) {
            alert('Preencha nome e email do novo síndico.');
            return;
        }
        if (!window.confirm(`Tem certeza que deseja promover ${newSindicoData.nome} a Síndico? O síndico atual será rebaixado.`)) {
            return;
        }

        try {
            const token = await auth.currentUser.getIdToken();
            const response = await fetch(`${getApiUrl()}/api/admin/change-sindico`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    condominioId: selectedCondo.id,
                    newSindicoNome: newSindicoData.nome,
                    newSindicoEmail: newSindicoData.email
                })
            });

            if (!response.ok) {
                const errorText = await response.text();
                let errorMsg = 'Falha ao trocar síndico';
                try {
                    const errorJson = JSON.parse(errorText);
                    errorMsg = errorJson.error || errorMsg;
                } catch (e) {
                    errorMsg = `${errorMsg} (Status: ${response.status} - ${errorText})`;
                }
                throw new Error(errorMsg);
            }

            alert('Síndico alterado com sucesso!');
            setNewSindicoData({ nome: '', email: '' });
            fetchCurrentSindico(selectedCondo.id); // Refresh current sindico display
        } catch (error) {
            console.error('Erro:', error);
            alert('Erro: ' + error.message);
        }
    };

    const handleSave = async () => {
        try {
            const token = await auth.currentUser.getIdToken();
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            };

            if (isEdit) {
                const response = await fetch(`${getApiUrl()}/api/admin/update-condo-info/${selectedCondo.id}`, {
                    method: 'PUT',
                    headers,
                    body: JSON.stringify(formData)
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error || 'Falha ao atualizar condomínio');
                }
                alert('Condomínio atualizado!');
            } else {
                const response = await fetch(`${getApiUrl()}/api/admin/create-condo-setup`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify(formData)
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error || 'Falha ao criar condomínio');
                }
                const data = await response.json();
                alert(`Condomínio criado com sucesso! Link enviado para o síndico.`);
            }
            setOpenDialog(false);
        } catch (error) {
            console.error(error);
            alert('Erro na operação: ' + error.message);
        }
    };

    const handleToggleStatus = async (condo) => {
        const newStatus = condo.status === 'ativo' ? 'suspenso' : 'ativo';
        const confirmMsg = newStatus === 'suspenso'
            ? `Tem certeza que deseja SUSPENDER o condomínio ${condo.nome}? Ninguém conseguirá acessar o sistema.`
            : `Deseja ATIVAR o condomínio ${condo.nome}?`;

        if (!window.confirm(confirmMsg)) return;

        try {
            const token = await auth.currentUser.getIdToken();
            const response = await fetch(`${getApiUrl()}/api/admin/update-condo-status/${condo.id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ status: newStatus })
            });
            if (!response.ok) throw new Error('Falha ao alterar status');
        } catch (error) {
            alert('Erro ao alterar status: ' + error.message);
        }
    };

    const formatData = (isoDate) => {
        if (!isoDate) return '-';
        // simple handling for YYYY-MM-DD string
        return isoDate;
    };

    return (
        <Box>
            <Box component="main" sx={{ flexGrow: 1, p: 3, background: 'linear-gradient(45deg, #1a237e 30%, #283593 90%)', minHeight: '100vh' }}>
                <Typography variant="h4" gutterBottom sx={{ color: 'white' }}>Gerenciar Condomínios (SaaS)</Typography>

                <Box sx={{ mb: 3 }}>
                    <Button variant="contained" color="secondary" startIcon={<AddIcon />} onClick={handleOpenCreate}>
                        Novo Condomínio
                    </Button>
                </Box>

                <Card className={styles.card}>
                    <CardContent>
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Nome</TableCell>
                                        <TableCell>CNPJ</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Contrato (Início/Fim)</TableCell>
                                        <TableCell align="right">Ações</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {condominios.map((condo) => (
                                        <TableRow key={condo.id}>
                                            <TableCell>
                                                <Typography variant="subtitle1" fontWeight="bold">{condo.nome}</Typography>
                                                <Typography variant="caption" color="text.secondary">{condo.endereco}</Typography>
                                            </TableCell>
                                            <TableCell>{condo.cnpj}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={condo.status === 'ativo' ? 'Ativo' : 'Suspenso'}
                                                    color={condo.status === 'ativo' ? 'success' : 'error'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {formatData(condo.inicio_contrato)} até {formatData(condo.fim_contrato)}
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton color="primary" onClick={() => handleOpenEdit(condo)}>
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    color={condo.status === 'ativo' ? 'error' : 'success'}
                                                    onClick={() => handleToggleStatus(condo)}
                                                    title={condo.status === 'ativo' ? 'Suspender' : 'Ativar'}
                                                >
                                                    {condo.status === 'ativo' ? <BlockIcon /> : <CheckCircleIcon />}
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>

                {/* Dialog Create/Edit */}
                <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
                    <DialogTitle>{isEdit ? 'Editar Condomínio' : 'Novo Condomínio'}</DialogTitle>
                    <DialogContent>
                        <Box sx={{ mt: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth label="Nome do Condomínio" value={formData.nome} onChange={(e) => setFormData({ ...formData, nome: e.target.value })} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth label="CNPJ" value={formData.cnpj} onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField fullWidth label="Endereço Completo" value={formData.endereco} onChange={(e) => setFormData({ ...formData, endereco: e.target.value })} />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth label="Início do Contrato" type="date"
                                        InputLabelProps={{ shrink: true }}
                                        value={formData.inicio_contrato} onChange={(e) => setFormData({ ...formData, inicio_contrato: e.target.value })}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth label="Fim do Contrato" type="date"
                                        InputLabelProps={{ shrink: true }}
                                        value={formData.fim_contrato} onChange={(e) => setFormData({ ...formData, fim_contrato: e.target.value })}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Email da Administradora (Opcional)"
                                        placeholder="email@administradora.com"
                                        value={formData.administradora_email}
                                        onChange={(e) => setFormData({ ...formData, administradora_email: e.target.value })}
                                        helperText="Se preenchido, este usuário terá acesso para gerenciar o estoque deste condomínio."
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Mensagem de Suspensão (Exibida ao usuário)"
                                        multiline rows={2}
                                        helperText="Esta mensagem aparecerá para os usuários se o condomínio estiver suspenso."
                                        value={formData.mensagem_suspensao}
                                        onChange={(e) => setFormData({ ...formData, mensagem_suspensao: e.target.value })}
                                    />
                                </Grid>

                                {!isEdit && (
                                    <>
                                        <Grid item xs={12}>
                                            <Typography variant="h6" sx={{ mt: 2 }}>Dados do Primeiro Síndico</Typography>
                                            <Typography variant="caption" color="text.secondary">Será criado um usuário com perfil de síndico para administrar este condomínio.</Typography>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField fullWidth label="Nome do Síndico" value={formData.sindico_nome} onChange={(e) => setFormData({ ...formData, sindico_nome: e.target.value })} />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField fullWidth label="Email do Síndico" type="email" value={formData.sindico_email} onChange={(e) => setFormData({ ...formData, sindico_email: e.target.value })} />
                                        </Grid>
                                    </>
                                )}

                                {isEdit && (
                                    <Grid item xs={12} sx={{ mt: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 2, backgroundColor: '#f9f9f9' }}>
                                        <Typography variant="h6" gutterBottom color="primary">Trocar Síndico</Typography>

                                        {currentSindico ? (
                                            <Typography variant="body2" sx={{ mb: 2 }}>
                                                <strong>Síndico Atual:</strong> {currentSindico.nome} ({currentSindico.email})
                                            </Typography>
                                        ) : (
                                            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                                                Não foi possível identificar o síndico atual.
                                            </Typography>
                                        )}

                                        <Typography variant="caption" color="textSecondary" paragraph>
                                            Informe os dados do novo síndico. Se o email já existir, o usuário será promovido.
                                        </Typography>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} md={5}>
                                                <TextField
                                                    fullWidth label="Nome do Novo Síndico" size="small"
                                                    value={newSindicoData.nome}
                                                    onChange={(e) => setNewSindicoData({ ...newSindicoData, nome: e.target.value })}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={5}>
                                                <TextField
                                                    fullWidth label="Email do Novo Síndico" size="small" type="email"
                                                    value={newSindicoData.email}
                                                    onChange={(e) => setNewSindicoData({ ...newSindicoData, email: e.target.value })}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={2}>
                                                <Button variant="contained" color="warning" fullWidth onClick={handleChangeSindico}>
                                                    Trocar
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
                        <Button variant="contained" onClick={handleSave}>Salvar</Button>
                    </DialogActions>
                </Dialog>

            </Box>
        </Box >
    );
};

export default GerenciarCondominios;
